import { Fragment, useState } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    Heading2,
    Heading3,
    Italic,
    Link,
    List,
    ListOrdered,
    Redo2,
    Underline,
    Undo2,
    type LucideIcon,
} from 'lucide-react';
import { Button, Tooltip } from '@shared/components';
import { RichTextLinkDialog } from './RichTextLinkDialog';
import type { RichTextConfig, RichTextFeature } from './richTextExtensions';
import { ALIGNABLE_NODES, type TextAlignment } from './textAlignClass';

interface RichTextToolbarProps {
    editor: Editor;
    config: RichTextConfig;
}

interface ToolbarAction {
    label: string;
    icon: LucideIcon;
    isActive?: boolean;
    disabled?: boolean;
    run: () => void;
}

interface ToolbarGroup {
    feature: RichTextFeature;
    actions: ToolbarAction[];
}

export function RichTextToolbar({ editor, config }: RichTextToolbarProps) {
    const [linkOpen, setLinkOpen] = useState(false);
    const state = useEditorState({
        editor,
        selector: ({ editor: current }) => ({
            bold: current.isActive('bold'),
            italic: current.isActive('italic'),
            underline: current.isActive('underline'),
            h2: current.isActive('heading', { level: 2 }),
            h3: current.isActive('heading', { level: 3 }),
            bulletList: current.isActive('bulletList'),
            orderedList: current.isActive('orderedList'),
            alignLeft: current.isActive({ textAlign: 'left' }),
            alignCenter: current.isActive({ textAlign: 'center' }),
            alignRight: current.isActive({ textAlign: 'right' }),
            link: current.isActive('link'),
            linkHref: (current.getAttributes('link').href as string | undefined) ?? '',
            canUndo: current.can().undo(),
            canRedo: current.can().redo(),
        }),
    });

    const chain = () => editor.chain().focus();
    const align = (textAlign: TextAlignment) => {
        const command = chain();
        ALIGNABLE_NODES.forEach((type) => command.updateAttributes(type, { textAlign }));
        command.run();
    };
    const groups: ToolbarGroup[] = [
        {
            feature: 'marks',
            actions: [
                { label: 'Bold', icon: Bold, isActive: state.bold, run: () => chain().toggleBold().run() },
                { label: 'Italic', icon: Italic, isActive: state.italic, run: () => chain().toggleItalic().run() },
                { label: 'Underline', icon: Underline, isActive: state.underline, run: () => chain().toggleUnderline().run() },
            ],
        },
        {
            feature: 'headings',
            actions: [
                { label: 'Heading 2', icon: Heading2, isActive: state.h2, run: () => chain().toggleHeading({ level: 2 }).run() },
                { label: 'Heading 3', icon: Heading3, isActive: state.h3, run: () => chain().toggleHeading({ level: 3 }).run() },
            ],
        },
        {
            feature: 'lists',
            actions: [
                { label: 'Bulleted list', icon: List, isActive: state.bulletList, run: () => chain().toggleBulletList().run() },
                { label: 'Numbered list', icon: ListOrdered, isActive: state.orderedList, run: () => chain().toggleOrderedList().run() },
            ],
        },
        {
            feature: 'alignment',
            actions: [
                { label: 'Align left', icon: AlignLeft, isActive: state.alignLeft, run: () => align('left') },
                { label: 'Align centre', icon: AlignCenter, isActive: state.alignCenter, run: () => align('center') },
                { label: 'Align right', icon: AlignRight, isActive: state.alignRight, run: () => align('right') },
            ],
        },
        {
            feature: 'link',
            actions: [{ label: 'Link', icon: Link, isActive: state.link, run: () => setLinkOpen(true) }],
        },
        {
            feature: 'history',
            actions: [
                { label: 'Undo', icon: Undo2, disabled: !state.canUndo, run: () => chain().undo().run() },
                { label: 'Redo', icon: Redo2, disabled: !state.canRedo, run: () => chain().redo().run() },
            ],
        },
    ];
    const enabledGroups = groups.filter((group) => config.features.includes(group.feature));

    const applyLink = (url: string) => {
        chain().extendMarkRange('link').setLink({ href: url }).run();
        setLinkOpen(false);
    };
    const removeLink = () => {
        chain().extendMarkRange('link').unsetLink().run();
        setLinkOpen(false);
    };

    return (
        <div role="toolbar" aria-label="Text formatting" className="flex flex-wrap gap-1 rounded-t-lg border-b border-mp-border bg-mp-background p-2">
            {enabledGroups.map((group, index) => (
                <Fragment key={group.feature}>
                    {index > 0 && <div role="separator" aria-orientation="vertical" className="mx-1 w-px self-stretch bg-mp-input" />}
                    {group.actions.map(({ label, icon: Icon, isActive, disabled, run }) => (
                        <Tooltip key={label} content={label}>
                            <Button
                                type="button"
                                size="xs"
                                variant={isActive ? 'primary' : 'clear'}
                                ariaLabel={label}
                                ariaPressed={isActive}
                                disabled={disabled}
                                startIcon={<Icon className="size-4" aria-hidden="true" />}
                                onClick={run}
                            />
                        </Tooltip>
                    ))}
                </Fragment>
            ))}
            {linkOpen && (
                <RichTextLinkDialog
                    initialUrl={state.linkHref}
                    linkPolicy={config.linkPolicy}
                    onSubmit={applyLink}
                    onRemove={removeLink}
                    onClose={() => setLinkOpen(false)}
                />
            )}
        </div>
    );
}
